'use client';
import { LegalShell, LegalSection, EntityCard, useLang, type Lang } from '@/components/LegalShell';

const C: Record<Lang, { title: string; sub: string; updated: string; s: { h: string; p: string[] }[]; entityNote: string }> = {
  uk: {
    title: 'Публічна оферта', updated: '18.07.2026',
    sub: 'Договір публічної оферти про надання цифрових товарів і послуг на сайті gamessales.vip.',
    entityNote: 'Оператор приймання платежів:',
    s: [
      { h: '1. Загальні положення', p: [
        'Цей документ є офіційною публічною пропозицією (офертою) магазину GamesSales (далі — «Продавець») укласти договір купівлі-продажу цифрових товарів і послуг на умовах, викладених нижче.',
        'Оформлення замовлення на сайті gamessales.vip означає повне й беззастережне прийняття (акцепт) умов цієї оферти.' ] },
      { h: '2. Предмет договору', p: [
        'Продавець надає покупцю цифрові товари та послуги: ключі активації ігор і доповнень (DLC), gift-коди, доступ до ігрових акаунтів, оренду ігор та супутні цифрові послуги для платформи Xbox / Microsoft.',
        'Товар вважається доставленим у момент передачі покупцю ключа / даних доступу на вказаний email або в особистому кабінеті.' ] },
      { h: '3. Ціна та оплата', p: [
        'Ціни вказані у валюті, що відповідає обраній мові інтерфейсу (₴ / ₽ / $). Оплата здійснюється через підключені платіжні сервіси у момент оформлення замовлення.',
        'До суми може додаватися комісія обраного способу оплати, яка відображається до підтвердження платежу.' ] },
      { h: '4. Доставка', p: [
        'Цифровий товар доставляється автоматично або менеджером протягом 1–24 годин після підтвердження оплати на email, вказаний при оформленні.' ] },
      { h: '5. Повернення', p: [
        'Оскільки товар є цифровим і вважається використаним у момент передачі ключа/даних, повернення можливе лише якщо товар не був переданий покупцю або не працює з вини Продавця. Детально — у розділі «Повернення».' ] },
      { h: '6. Відповідальність', p: [
        'Продавець не несе відповідальності за неможливість активації товару через блокування регіону, порушення покупцем правил платформи Microsoft чи хибно вказані дані.' ] },
    ],
  },
  ru: {
    title: 'Публичная оферта', updated: '18.07.2026',
    sub: 'Договор публичной оферты о предоставлении цифровых товаров и услуг на сайте gamessales.vip.',
    entityNote: 'Оператор приёма платежей:',
    s: [
      { h: '1. Общие положения', p: [
        'Настоящий документ является официальным публичным предложением (офертой) магазина GamesSales (далее — «Продавец») заключить договор купли-продажи цифровых товаров и услуг на изложенных ниже условиях.',
        'Оформление заказа на сайте gamessales.vip означает полное и безоговорочное принятие (акцепт) условий данной оферты.' ] },
      { h: '2. Предмет договора', p: [
        'Продавец предоставляет покупателю цифровые товары и услуги: ключи активации игр и дополнений (DLC), gift-коды, доступ к игровым аккаунтам, аренду игр и сопутствующие цифровые услуги для платформы Xbox / Microsoft.',
        'Товар считается доставленным в момент передачи покупателю ключа / данных доступа на указанный email или в личном кабинете.' ] },
      { h: '3. Цена и оплата', p: [
        'Цены указаны в валюте, соответствующей выбранному языку интерфейса (₴ / ₽ / $). Оплата осуществляется через подключённые платёжные сервисы в момент оформления заказа.',
        'К сумме может добавляться комиссия выбранного способа оплаты, которая отображается до подтверждения платежа.' ] },
      { h: '4. Доставка', p: [
        'Цифровой товар доставляется автоматически или менеджером в течение 1–24 часов после подтверждения оплаты на email, указанный при оформлении.' ] },
      { h: '5. Возврат', p: [
        'Поскольку товар является цифровым и считается использованным в момент передачи ключа/данных, возврат возможен только если товар не был передан покупателю или не работает по вине Продавца. Подробно — в разделе «Возврат».' ] },
      { h: '6. Ответственность', p: [
        'Продавец не несёт ответственности за невозможность активации товара из-за блокировки региона, нарушения покупателем правил платформы Microsoft или неверно указанных данных.' ] },
    ],
  },
  en: {
    title: 'Public Offer', updated: '2026-07-18',
    sub: 'Public offer agreement for the provision of digital goods and services on gamessales.vip.',
    entityNote: 'Payment acceptance operator:',
    s: [
      { h: '1. General', p: [
        'This document is the official public offer of the GamesSales store (the “Seller”) to conclude a sale agreement for digital goods and services under the terms below.',
        'Placing an order on gamessales.vip constitutes full and unconditional acceptance of this offer.' ] },
      { h: '2. Subject', p: [
        'The Seller provides digital goods and services: game and DLC activation keys, gift codes, access to game accounts, game rentals and related digital services for the Xbox / Microsoft platform.',
        'Goods are deemed delivered upon transfer of the key / access details to the buyer via the provided email or personal account.' ] },
      { h: '3. Price & payment', p: [
        'Prices are shown in the currency matching the selected interface language (₴ / ₽ / $). Payment is processed via the connected payment services at checkout.',
        'A selected payment method fee may be added to the total and is shown before payment confirmation.' ] },
      { h: '4. Delivery', p: [
        'Digital goods are delivered automatically or by a manager within 1–24 hours after payment confirmation to the email provided at checkout.' ] },
      { h: '5. Refunds', p: [
        'As the goods are digital and considered used upon transfer of the key/details, a refund is possible only if the goods were not delivered or do not work through the Seller’s fault. See the “Refunds” section.' ] },
      { h: '6. Liability', p: [
        'The Seller is not liable for the inability to activate goods due to region blocking, the buyer violating Microsoft platform rules, or incorrectly provided data.' ] },
    ],
  },
};

export default function OfferPage() {
  const lang = useLang();
  const c = C[lang];
  return (
    <LegalShell title={c.title} subtitle={c.sub} updated={c.updated}>
      {c.s.map(sec => (
        <LegalSection key={sec.h} heading={sec.h}>
          {sec.p.map((t, i) => <p key={i} style={{ margin: i ? '10px 0 0' : 0 }}>{t}</p>)}
        </LegalSection>
      ))}
      <LegalSection heading={c.entityNote}><EntityCard /></LegalSection>
    </LegalShell>
  );
}
